// La medición de la campaña: Cloudflare Web Analytics y el Pixel de Meta.
//
// ═══ LO QUE NO SE MIDE, QUE ES TODO EL PUNTO DE ESTE ARCHIVO ═══
//
// Las páginas de reserva —holaturnos.com/<slug>— son clientes de nuestros
// clientes. Alguien que entra a sacarse un turno en una barbería no aceptó
// nada nuestro y no tiene por qué cargar con la publicidad de HolaTurnos. El
// panel por dentro y el admin tampoco se miden: ahí no hay nada que medir, hay
// gente trabajando.
//
// `index.html` es el de TODA la aplicación: el panel, el admin, el registro y
// las reservas comparten documento. Por eso los dos snippets no están ahí,
// donde los cargaría cualquiera que abra cualquier cosa, sino que los inyecta
// este módulo y sólo en las rutas de la lista.
//
// ─── Por qué se decide una sola vez, al cargar el documento ─────────────────
//
// Y no en cada cambio de ruta, que sería lo obvio. Porque la lista de abajo
// tiene una propiedad que un filtro por ruta no tendría: de `/registro` sólo
// se sale con un `window.location.assign` al panel, que es una recarga entera.
// El documento muere y con él los scripts. Así "no medir el panel por dentro"
// no depende de que nadie se olvide de apagar nada: depende de que nunca se
// prendió.
//
// `/panel/login` quedó afuera por exactamente lo contrario. Ahí el login entra
// con el router, el documento sobrevive, y lo que se hubiera cargado seguiría
// vivo adentro del panel. Además nadie llega al login desde un anuncio.
//
// El corolario incómodo: si alguien llega a `/registro` navegando con el
// router en vez de cargando el documento, no se mide. El único link así que
// existe es el "¿Todavía no tenés cuenta?" del login, y lleva `reloadDocument`
// justamente por esto. Si mañana aparece otro, tiene que llevarlo también.

import { capturarUtm } from './utm.js';

const PIXEL_META = '1666232728457187';
const TOKEN_CLOUDFLARE = '760c4546e0024edc9c9e6a39082dcfb4';

/** Si esta pantalla se midió. Lo fija arrancarMedicion() y no cambia después. */
let medida = false;

/**
 * Las únicas rutas de la aplicación que se miden. Ver el comentario de arriba.
 *
 * `/panel/clave/<token>` es la pantalla donde se elige la contraseña desde el
 * mail, y entra por una razón concreta: es donde se sabe que esa cuenta se
 * puede usar de verdad. El alta sola, con tres campos, incluye gente que nunca
 * va a volver.
 *
 * Es la única ruta de /panel que se mide, y se mide SOLAMENTE esa pantalla: de
 * ahí se sale con una recarga entera al panel, así que el documento muere y
 * con él los scripts. El panel por dentro sigue sin medirse.
 */
const RUTAS_MEDIDAS = new Set(['/registro']);

/** El prefijo de la pantalla de la contraseña. Lleva el token adentro. */
const PREFIJO_CLAVE = '/panel/clave/';

/** La landing y las legales no pasan por acá: son HTML suelto y lo llevan adentro. */
export function rutaMedida(pathname) {
  // Sin la barra final: /registro y /registro/ son la misma pantalla.
  // En minúsculas porque el router de React matchea sin distinguir mayúsculas:
  // /Registro dibuja el formulario igual, y sin esto el alta de alguien que
  // tipeó la URL con mayúscula no se contaría.
  const limpia = String(pathname ?? '').toLowerCase().replace(/\/+$/, '') || '/';
  if (RUTAS_MEDIDAS.has(limpia)) return true;
  // Con el token adentro, así que es por prefijo y no por igualdad.
  return limpia.startsWith(PREFIJO_CLAVE) && limpia.length > PREFIJO_CLAVE.length;
}

// ═══════════════════════════════════════════════════════════════════════════
//  Los dos snippets
// ═══════════════════════════════════════════════════════════════════════════

function cargarCloudflare() {
  // El beacon estándar. No usa cookies y no identifica a nadie: cuenta visitas.
  const script = document.createElement('script');
  script.defer = true;
  script.src = 'https://static.cloudflareinsights.com/beacon.min.js';
  script.setAttribute('data-cf-beacon', JSON.stringify({ token: TOKEN_CLOUDFLARE }));
  document.head.appendChild(script);
}

function cargarMeta() {
  if (window.fbq) return;

  /* El snippet oficial, tal cual lo da Meta. Se deja con su forma original a
     propósito: reformatearlo sólo hace más difícil comparar contra el que
     publican el día que haya que actualizarlo. */
  /* eslint-disable */
  !function (f, b, e, v, n, t, s) {
    if (f.fbq) return; n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
    };
    if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0';
    n.queue = []; t = b.createElement(e); t.async = !0;
    t.src = v; s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s)
  }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */

  // Apaga la recolección automática de Meta, que si no se mete sola a leer los
  // clics y los campos de los formularios. Acá eso significaría el formulario
  // de registro, o sea el nombre, el email y el teléfono de quien se está
  // dando de alta. Los únicos eventos que salen son los dos que elegimos.
  // Va ANTES del init.
  window.fbq('set', 'autoConfig', false, PIXEL_META);
  window.fbq('init', PIXEL_META);
  window.fbq('track', 'PageView');
}

/**
 * Arranca la medición si la ruta del documento está en la lista.
 *
 * Se llama una vez, desde main.jsx, antes de montar React.
 *
 * La captura de los UTM cuelga de esta MISMA compuerta y no de una propia.
 * Guardar en localStorage es rastrear tanto como cargar un pixel, y
 * privacidad.html promete que en las páginas de reserva no pasa ninguna de las
 * dos cosas. Una sola compuerta es una sola cosa que mantener cierta.
 */
export function arrancarMedicion() {
  if (!rutaMedida(window.location?.pathname)) return;
  medida = true;
  capturarUtm();
  cargarMeta();
  cargarCloudflare();
}

// ═══════════════════════════════════════════════════════════════════════════
//  La conversión
// ═══════════════════════════════════════════════════════════════════════════

// Una sola vez por documento. El alta ya se recarga enseguida, así que esto es
// un cinturón además del tirador: dos CompleteRegistration por una sola cuenta
// ensucian el costo por conversión, que es el número con el que se decide
// cuánta plata poner en la campaña.
let yaAvisado = false;

/**
 * El id del evento de conversión, o null si esta pantalla no se mide.
 *
 * Se pide ANTES de mandar el alta, no después, y por una razón concreta: el
 * mismo id tiene que viajar en el evento del navegador y en el de la API de
 * conversiones del lado del servidor, para que Meta los cuente como UNO y no
 * como dos. Y la única forma de que el servidor lo conozca es que vaya en el
 * cuerpo del alta. Generarlo recién al recibir el 201 lo dejaría encerrado en
 * un navegador que se cierra dos segundos después.
 *
 * Devuelve null cuando no hay pixel: mandarle al servidor el id de un evento
 * que nunca se disparó sería guardar una correlación con la nada.
 */
export function idDeConversion() {
  if (!medida) return null;
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  // Navegadores viejos, o un contexto sin https: alcanza con que no se repita.
  return `reg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * El alta salió bien. Se llama con la cuenta YA creada, no al tocar el botón:
 * un formulario enviado no es una conversión hasta que el servidor dice 201.
 *
 * @param {string|null} eventID el de idDeConversion(), ya guardado en el alta
 * @returns {Promise<void>} resuelve cuando se le dio al evento tiempo de salir
 */
/**
 * Eligió su contraseña y entró: la cuenta se puede usar de verdad.
 *
 * Evento APARTE y no en reemplazo de CompleteRegistration. Son dos cosas
 * distintas y las dos sirven:
 *
 *   CompleteRegistration  llenó el formulario. Es a lo que la campaña está
 *                         optimizando hoy y no se toca: cambiarlo la reinicia.
 *   ActivateAccount       además volvió por el mail. Es el número real.
 *
 * Sin eventID: éste no tiene gemelo del lado del servidor que haya que
 * deduplicar. El día que exista la API de conversiones, se le agrega.
 */
let yaActivado = false;

export function verificacionCompletada() {
  if (yaActivado || typeof window.fbq !== 'function') return Promise.resolve();
  yaActivado = true;

  window.fbq('trackCustom', 'ActivateAccount');

  // Lo mismo que abajo: con un bloqueador, fbq existe pero no manda nada, y
  // esperar 250 ms por un evento encolado que la recarga se lleva puesto es
  // regalarle un cuarto de segundo a alguien que está entrando a su panel.
  if (typeof window.fbq.callMethod !== 'function') return Promise.resolve();
  return new Promise((listo) => { setTimeout(listo, 250); });
}

export function registroCompletado(eventID) {
  if (yaAvisado || typeof window.fbq !== 'function') return Promise.resolve();
  yaAvisado = true;

  window.fbq('track', 'CompleteRegistration', {}, eventID ? { eventID } : undefined);

  // ─── Por qué se mira callMethod y no fbq a secas ──────────────────────────
  //
  // El snippet de Meta define window.fbq de forma sincrónica como un empujador
  // de cola, exista o no fbevents.js. Con un bloqueador —uBlock, Brave, el DNS
  // del lugar— `typeof window.fbq === 'function'` sigue dando true: el evento
  // se encola, la recarga se lo lleva puesto, y de yapa se pagaron 250 ms de
  // espera por nada. `callMethod` sólo aparece cuando la librería de verdad
  // llegó a cargar, así que es lo que distingue "va a salir" de "no va a salir".
  if (typeof window.fbq.callMethod !== 'function') return Promise.resolve();

  // Meta manda el evento con un beacon y no avisa cuándo salió. Como acá
  // inmediatamente después se recarga la página entera, sin esta pausa el
  // navegador cancela el pedido a mitad de camino y se pierde la única
  // conversión que importa. Un cuarto de segundo, una vez en la vida de la
  // cuenta.
  return new Promise((listo) => { setTimeout(listo, 250); });
}
